import {AfterViewInit, ChangeDetectorRef, Component, inject, model, OnInit, signal, ViewChild} from '@angular/core';
import {
  ButtonComponent,
  ButtonOptions,
  DateComponent, DateOptions, FormComponent,
  InputComponent,
  ModalComponent,
  ModalOptions
} from "@shared/components";
import {DropdownComponent, DropdownOptions} from "@shared/components/dropdown/dropdown.component";
import {
  AbstractControl,
  FormControl,
  FormGroup,
  FormsModule,
  NgForm,
  ReactiveFormsModule, ValidationErrors,
  ValidatorFn,
  Validators
} from "@angular/forms";
import {NgOptimizedImage} from "@angular/common";
import {assets} from '@core/constants';
import {Coding} from '@core/classes/other/coding';
import {APIService, DataService, ToastService} from '@core/services';
import {PatientService} from '@core/services/patient.service';
import {HTTPMethods, ModalTypes} from '@core/enums';
import {ATC} from '@core/classes/other/atc';
import {HttpHeaders} from '@angular/common/http';
import {DefaultSearchComponent} from '@shared/components/dropdown/search/default-search/default-search.component';
import {lastValueFrom} from 'rxjs';
import {Response, ResponseSingle} from '@core/classes/other/response';
import {SocialHistory} from '@core/classes/tables/social-history';
import moment from 'moment/moment';
import {checkDate} from '@core/helpers';

@Component({
  selector: 'ncp-add-social-history',
  imports: [
    ButtonComponent,
    DateComponent,
    DropdownComponent,
    FormsModule,
    ModalComponent,
    NgOptimizedImage,
    ReactiveFormsModule
  ],
  templateUrl: './add-social-history.component.html',
  styleUrl: './add-social-history.component.scss',
})
export class AddSocialHistoryComponent implements OnInit, AfterViewInit {
  @ViewChild('mod') public mod!: ModalComponent;
  @ViewChild('shFrm') protected socialHistoryFormEl?: NgForm;
  @ViewChild('addSocialHistoryButton') public addSocialHistoryButton!: ButtonComponent;

  @ViewChild( 'socialHistoryDesc') public socialHistoryDesc!: DropdownComponent<Coding>;
  @ViewChild('startDate') public startDate!: DateComponent;
  @ViewChild('endDate') public endDate!: DateComponent;

  @ViewChild('addSocialHistoryButton') getSPbtn  = () => {
    return this.addSocialHistoryButton
  }

  protected readonly toastService = inject(ToastService);
  private readonly changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);

  private apiService = inject(APIService);
  private patientService = inject(PatientService);
  private dataService = inject(DataService);

  protected data = model<any>();

  private _params = {};
  protected form: FormGroup = new FormGroup({
    coding_id: new FormControl<number | string | undefined>(undefined, [Validators.required]),
    start_date: new FormControl<string>('', [Validators.required]),
    end_date: new FormControl<string | null | undefined>(null, []),
  }, {
    validators: [this.formValidator()]
  });

  formValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      let errors: ValidationErrors | null = {};

      let codingId = (control as FormGroup).controls['coding_id'];
      let startDate = (control as FormGroup).controls['start_date'];
      let endDate = (control as FormGroup).controls['end_date'];

      let patientDOB = moment(this.patientService.data?.dob, ['YYYY-MM-DD', 'YYYY/MM/DD', 'YYYY.MM.DD', 'DD-MM-YYYY', 'DD/MM/YYYY', 'DD.MM.YYYY'], true);
      let today = moment();

      if(codingId.value === undefined || codingId.value === null) {
        codingId.setErrors({sh_codingIdInvalid: true});
        errors['sh_codingIdInvalid'] = true;
      }

      if(!checkDate(startDate.value).isValid()) {
        startDate.setErrors({sh_startDateFormatInvalid: true});
        errors['sh_startDateInvalid'] = true;
      // } else if(!checkDate(endDate.value).isValid()) {
      //   endDate.setErrors({sh_endDateFormatInvalid: true});
      //   errors['sh_endDateInvalid'] = true;
      }

      if(checkDate(startDate.value).isValid() && checkDate(endDate.value).isValid()) {
        if(!checkDate(startDate.value).isSameOrBefore(checkDate(endDate.value))) {
          startDate.setErrors({sh_startDateBeforeEndDate: true});
          endDate.setErrors({sh_endDateAfterStartDate: true});
          errors['sh_onSetNotAfterEnd'] = true;
        } else {
          if(checkDate(startDate.value).isSameOrBefore(patientDOB)) {
            startDate.setErrors({sh_onSetDateAfterDOB: true});
            errors['sh_startNotAfterPatientDoB'] = true;
          } else if(checkDate(endDate.value).isSameOrAfter(today)) {
            endDate.setErrors({sh_endDateBeforeToday: true});
            errors['sh_endDateNotBeforeToday'] = true;
          } else {
            startDate.setErrors(null);
            endDate.setErrors(null);
          }
        }
      }

      if(endDate.value && !checkDate(endDate.value).isValid()) {
        endDate.setErrors({sh_endDateFormatInvalid: true});
        errors['sh_endDateInvalid'] = true;
      }

      return Object.keys(errors).length > 0 ? errors : null;
    }
  }

  protected shLoading: boolean = false;

  public btnSHOptions = signal<ButtonOptions>(
    new ButtonOptions({
      id: 'ndaBtn',
      tooltip: 'add_social_history',
    })
  );

  protected modOptions = model<ModalOptions>(
    new ModalOptions({
      id: 'modal',
      title: 'add_social_history',
      type: ModalTypes.form,
      form: FormComponent,
      options: {
        animation: true,
        size: 'xl',
        fullscreen: "lg",
        backdrop: 'static',
        keyboard: false,
      },
      onClose: () => {this.onFormReset()}
    })
  );

  protected socialHistoryDescDDOptions = signal<DropdownOptions<Coding | ATC>>(
    new DropdownOptions<Coding | ATC>({
      id: 'socialHistoryDescription',
      name: 'socialHistoryDescription',
      placeholder: 'social_history_description',
      label: {
        id: "social_history_desc_label",
        text: 'social_history_description',
      },
      api: {
        path: '/get-all_codings',
        headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
        body: {
          oid: '1.3.6.1.4.1.12559.11.10.1.3.1.42.14',
          description: ''
        },
      },
      required: true,
      searchComponent: DefaultSearchComponent,
      formControl: this.form.get('coding_id') as FormControl
    })
  );

  protected startDateOptions = signal<DateOptions>(
    new DateOptions({
      id: 'startDate',
      label: {
        text: 'start_date',
      },
      name: 'startDate',
      initialValue: '',
      required: true,
      formControl: this.form.get('start_date') as FormControl,
      minDate: moment(this.patientService.data?.dob).format('YYYY-MM-DD'),
      maxDate: moment().format('YYYY-MM-DD'),
    })
  );

  protected endDateOptions = signal<DateOptions>(
    new DateOptions({
      id: 'endDate',
      label: {
        text: 'end_date',
      },
      name: 'endDate',
      initialValue: '',
      formControl: this.form.get('end_date') as FormControl,
      minDate: moment(this.patientService.data?.dob).format('YYYY-MM-DD'),
      maxDate: moment().format('YYYY-MM-DD'),
      disabled: true,
      required: false,
    })
  );

  ngOnInit() {
    this.btnSHOptions().onClick = (event) => {
      this.mod.options().onReset = this.onFormReset;
      this.socialHistoryDesc.detectChanges();
      this.addSocialHistoryButton.blur();
      this.shLoading = true;
      this.downloadDropDownData(this.mod.options().title).then((value) => {
        this.shLoading = false;
        this.changeDetectorRef.detectChanges();
        this.mod.open();
      });
    };

    //this.startDateOptions().formControl = (this.form.get('start_date') as FormControl);
    //this.endDateOptions().formControl = (this.form.get('end_date') as FormControl);
    this.modOptions().onSave = this.onSave;
    this.modOptions().onShow = this.downloadDropDownData;

    this.form.valueChanges.subscribe(value => {
      if(
        value?.start_date &&
        checkDate(value?.start_date, true).isValid() &&
        this.form.get('start_date')?.valid
      ) {
        console.log(
          'value = ' + value?.start_date,
          'check = ' + checkDate(value?.start_date, true).isValid(),
          'onset valid = ' + this.form.get('start_date')?.valid,
          'end disabled = ' + this.form.get('end_date')?.disabled
        )
        this.endDate!.minDate! = {
          year: parseInt(moment(value?.start_date).format('YYYY'), 10),
          month: parseInt(moment(value?.start_date).format('M'), 10),
          day: parseInt(moment(value?.start_date).format('D'), 10),

        }
        if(this.form.get('end_date')?.disabled) {
          this.endDate?.enable();
        }
      }

      if(
        (value?.start_date &&
          !checkDate(value?.start_date, true).isValid() &&
          !this.form.get('start_date')?.valid) &&
        this.form.get('end_date')?.enabled
      ) {
        console.log(
          'value = ' + value?.start_date,
          'check = ' + checkDate(value?.start_date, true).isValid(),
          'onset valid = ' + this.form.get('start_date')?.valid,
          'end enabled = ' + this.form.get('end_date')?.enabled
        )
        this.endDate?.disable();
      }

      if(
        value?.end_date &&
        checkDate(value?.end_date, true).isValid() &&
        this.form.get('end_date')?.valid
      ) {
        console.log(
          'value = ' + value?.start_date,
          'check = ' + checkDate(value?.start_date, true).isValid(),
          'onset valid = ' + this.form.get('start_date')?.valid,
          'end disabled = ' + this.form.get('end_date')?.disabled
        )
        this.startDate!.maxDate! = {
          year: parseInt(moment(value?.end_date).format('YYYY'), 10),
          month: parseInt(moment(value?.end_date).format('M'), 10),
          day: parseInt(moment(value?.end_date).format('D'), 10),

        }
      }
    });
  }

  onFormReset = () => {
    console.log('on reset form');
    this.form.reset();
    this.socialHistoryDesc.clearSelection();
    this.startDate.reset();
    this.endDate.reset();

    this.modOptions().errors = [];
  }

  ngAfterViewInit(): void {

  }

  protected downloadDropDownData = async (title?: string) => {
    let ddData = JSON.parse(localStorage.getItem('dropDownData')!) as Object || {};

    if(ddData.hasOwnProperty('socialHistoryDescription')) {
      return;
    }

    const data = await lastValueFrom(this.apiService.call({
      path: '/get-all_codings',
      headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
      body: {
        oid: '1.3.6.1.4.1.12559.11.10.1.3.1.42.14',
        description: ''
      }
    })!);

    if (data && data.status === 1 && data.data.length > 0) {
      Object.defineProperty(ddData, 'socialHistoryDescription', {
        value: (data as Response<Coding>).data,
        enumerable: true,
        writable: false
      });
      this.socialHistoryDesc.data = (data as Response<Coding>).data || [];

      localStorage.setItem('dropDownData', JSON.stringify(ddData));
    }
  }

  onSave = () => {
    this.socialHistoryFormEl?.ngSubmit.emit();
  }

  validate = (): boolean => {
    Object.keys(this.form.controls).forEach((control) => {
      //if(this.form.hasValidator(Validators.required)) {
        this.form.controls[control].markAsTouched();
      //}
    })

    return true;
  }

  onFormSubmit = () => {
    if(this.validate() && this.form.invalid) {
      console.log('this.form.is.invalid');
      this.modOptions().errors = this.form.errors!;
      this.mod.detectChanges();
      return;
    }

    this.modOptions().disableOnSave = true;
    let body = {
      start_date: (this.form.get('start_date') as FormControl).value,
      end_date: (this.form.get('end_date') as FormControl).value,
      coding_id: this.socialHistoryDescDDOptions().value!.coding_id,
      status: 1,
      version_id: this.patientService.version!.new,
    }

    this.apiService.call({
      path: '/social-history',
      headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
      type: HTTPMethods.POST,
      body: body
    })?.subscribe((res: ResponseSingle<SocialHistory>) => {
      if(res && res.status?.toString() === '1') {
        let data = this.data()?.tblOptions().data
        data.unshift(res.data);
        if (this.data && this.data() && this.data().tbl && this.data().tbl.tableData) {
          this.data()!.tbl!.tableData! = data;
        }
        this.mod.close();
      }
    })
  }

  protected readonly assets = assets;
}
